import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, Lock, UserCog } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * T146 (spec 001 Phase 10) — Account profile page.
 *
 * Read-only display of email + created-at (from GET /auth/me). Change-
 * password flow is two-step:
 *   1. user confirms current password → POST /auth/reauth (sets
 *      session.reauthExpiresAt + a 5-minute window)
 *   2. user submits new password → POST /auth/change-password with
 *      `X-Reauth: 1` header. Backend bumps users.password_version which
 *      kills every other session (T146 in auth plugin).
 *
 * No name / avatar editing — the `users` table has no such columns and
 * those are explicitly out of scope per the task body.
 */
export function AccountProfile() {
  const me = useCurrentUser();

  return (
    <div className="pt-6 space-y-4">
      <div>
        <h1 className="text-lg font-semibold inline-flex items-center gap-2">
          <UserCog className="h-4 w-4 text-muted-foreground" /> Profile
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Your identity on this open-frontend instance.
        </p>
      </div>

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold">Profile</h3>
        </div>
        <div className="px-4 py-3 space-y-3">
          <Field label="Email">
            {me.data ? (
              <span className="font-mono text-sm break-all">{me.data.email}</span>
            ) : (
              <span className="text-xs text-muted-foreground">Loading…</span>
            )}
          </Field>
          <Field label="Member since">
            {me.data?.createdAt ? (
              <span className="text-sm">{new Date(me.data.createdAt).toLocaleDateString()}</span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </Field>
        </div>
      </div>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: async () => {
      if (newPw !== confirmPw) {
        throw new ApiError(400, "password_mismatch", "new passwords do not match");
      }
      if (newPw.length < 8) {
        throw new ApiError(400, "password_too_short", "new password must be at least 8 characters");
      }
      // Step 1: reauth with the current password — sets session window.
      await api.post(`/auth/reauth`, { password: currentPw });
      // Step 2: change-password with the X-Reauth marker.
      await api.post(`/auth/change-password`, { newPassword: newPw }, { headers: { "x-reauth": "1" } });
    },
    onSuccess: () => {
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setDone(true);
      setTimeout(() => setDone(false), 5000);
    },
  });

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-muted-foreground" /> Change password
        </h3>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="space-y-1">
          <Label htmlFor="current-pw" className="text-xs">Current password</Label>
          <Input
            id="current-pw"
            type="password"
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="new-pw" className="text-xs">New password</Label>
            <Input
              id="new-pw"
              type="password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
            <div className="text-2xs text-subtle-foreground">At least 8 characters.</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm-pw" className="text-xs">Confirm new password</Label>
            <Input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
            />
          </div>
        </div>
        {submit.error instanceof ApiError && (
          <div className="text-xs text-danger">{submit.error.message}</div>
        )}
        {done && (
          <div className="text-xs text-success inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" /> Password updated. Other devices have been signed out.
          </div>
        )}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => submit.mutate()}
            disabled={!currentPw || !newPw || !confirmPw || submit.isPending}
          >
            {submit.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Update password
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
